import { Calendar, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { LoadFilters, InstallerProfile } from "@/types/loads";

interface LoadsFiltersProps {
  filters: LoadFilters;
  onFiltersChange: (filters: LoadFilters) => void;
  installers: InstallerProfile[];
  hasActiveFilters: boolean;
}

export function LoadsFilters({ 
  filters, 
  onFiltersChange, 
  installers, 
  hasActiveFilters 
}: LoadsFiltersProps) {
  const handleDateChange = (date: Date | undefined) => {
    onFiltersChange({ ...filters, date });
  };

  const handleStatusChange = (status: string) => {
    onFiltersChange({ ...filters, status });
  };

  const handleInstallerChange = (installerId: string) => {
    onFiltersChange({ ...filters, installerId });
  };

  const clearFilters = () => {
    onFiltersChange({
      date: undefined,
      status: "all",
      installerId: "all",
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Suodata:</span>
      </div>
      
      {/* Date filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "justify-start text-left font-normal",
              !filters.date && "text-muted-foreground"
            )}
          >
            <Calendar className="mr-2 h-4 w-4" />
            {filters.date ? format(filters.date, "d.M.yyyy", { locale: fi }) : "Päivämäärä"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="single"
            selected={filters.date}
            onSelect={handleDateChange}
            initialFocus
            className="pointer-events-auto"
          />
        </PopoverContent>
      </Popover>

      {/* Status filter */}
      <Select value={filters.status} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-[150px] h-9">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Kaikki statukset</SelectItem>
          <SelectItem value="planned">Suunniteltu</SelectItem>
          <SelectItem value="in_progress">Käynnissä</SelectItem>
          <SelectItem value="completed">Valmis</SelectItem>
          <SelectItem value="cancelled">Peruutettu</SelectItem>
        </SelectContent>
      </Select>

      {/* Installer filter */}
      <Select value={filters.installerId} onValueChange={handleInstallerChange}>
        <SelectTrigger className="w-[180px] h-9">
          <SelectValue placeholder="Asentaja" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Kaikki asentajat</SelectItem>
          {installers.map((installer) => (
            <SelectItem key={installer.id} value={installer.id}>
              {`${installer.first_name || ""} ${installer.last_name || ""}`.trim() || "Nimetön"}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Clear filters button */}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
          <X className="h-4 w-4" />
          Tyhjennä
        </Button>
      )}
    </div>
  );
}
